import { useId } from 'react';
import { FloatingLabel, Form } from 'react-bootstrap';

interface EditNumberProps {
  /** Floating label + placeholder. Omit to render a bare control (the caller supplies its own label, e.g. via `Field`). */
  readonly label?: string;
  readonly value: number | null;
  readonly onChange: (value: number | null) => void;
  readonly mandatory?: boolean;
  readonly min?: number;
  readonly max?: number;
  readonly disabled?: boolean;
}

/**
 * Integer-input wrapper. Same shape as `EditString`/`EditText` —
 * `null` represents "not filled," empty input is normalised to
 * `null`, optional `mandatory` flag adds " *" and the HTML
 * `required` attribute. `min` / `max` map to the native input
 * constraints when set. Uses `parseInt(.., 10)`: non-numeric input is
 * coerced to `null` (browser type="number" prevents this in normal
 * use, but paste / programmatic input could otherwise leak NaN). When
 * `label` is omitted the bare control renders and the caller owns the label.
 */
export const EditNumber = ({ label, value, onChange, mandatory = false, min, max, disabled = false }: EditNumberProps) => {
  const id = useId();
  const control = (
    <Form.Control
      type="number"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') {
          onChange(null);
          return;
        }
        const parsed = parseInt(v, 10);
        onChange(Number.isNaN(parsed) ? null : parsed);
      }}
      placeholder={label}
      required={mandatory}
      min={min}
      max={max}
      disabled={disabled}
    />
  );
  // The id goes on the labelled branch only: a bare control takes its id from an enclosing `Form.Group` (e.g. `Field`), whose label points at it.
  return label ? (
    <FloatingLabel controlId={id} label={mandatory ? `${label} *` : label}>
      {control}
    </FloatingLabel>
  ) : (
    control
  );
};
